import {
  Anchor,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  getDefaultZIndex,
  Loader,
  Popover,
  Text,
  Tooltip,
} from "@mantine/core";
import dayjs from "dayjs";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmationButton from "src/components/shared/ConfirmationButton/ConfirmationButton";
import { useSelector } from "src/redux/hooks";
import type { IPendingRequest } from "src/redux/slices/pendingRequestsSlice";
import { getConsolidatedChecklistAction } from "src/utils/getPendingRequestLabel";
import {
  groupPendingRequests,
  type PendingRequestRun,
} from "src/utils/groupPendingRequests";
import { Notification } from "src/utils/Notification";

import styles from "./PendingRequestsInfo.module.scss";

const POPOVER_Z_INDEX = getDefaultZIndex("popover");
const TOOLTIP_Z_INDEX = getDefaultZIndex("max");
const pendingTooltipProps = {
  withinPortal: true,
  zIndex: TOOLTIP_Z_INDEX,
  floatingStrategy: "fixed" as const,
  portalProps: {
    reuseTargetNode: false,
    style: { zIndex: TOOLTIP_Z_INDEX },
  },
} as const;

const getStatusLabel = (status: string, hasServerEntryId: boolean) => {
  switch (status) {
    case "pending":
      return "Pending";
    case "uploading":
      return "Uploading";
    case "done":
      return "Done";
    case "failed":
      return "Failed";
    case "pending-new-entry":
      return hasServerEntryId
        ? "Waiting for earlier request"
        : "Waiting for entry";
    default:
      return status;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "done":
      return "green";
    case "failed":
      return "red";
    case "uploading":
      return "blue";
    case "pending-new-entry":
      return "gray";
    default:
      return "yellow";
  }
};

// Retry is only offered once a row has actually failed.
const isRetryable = (status: string) => status === "failed";

const UploadStatusBadge = ({ req }: { req: IPendingRequest }) => {
  const { status } = req;
  if (status === "uploading") {
    return (
      <Flex align="center" gap={6}>
        <Loader size="xs" />
        <Text size="xs">Uploading</Text>
      </Flex>
    );
  }

  return (
    <Badge size="xs" color={getStatusColor(status)} variant="light">
      {getStatusLabel(status, Boolean(req.serverEntryId))}
    </Badge>
  );
};

const getRunRequest = (run: PendingRequestRun): IPendingRequest => {
  const latest = run.requests[run.requests.length - 1];
  const representative =
    run.requests.find(request => request.status !== "done") ?? latest;

  return {
    ...representative,
    action:
      run.kind === "checklists"
        ? getConsolidatedChecklistAction(run.requests)
        : representative.action,
    created: latest.created,
    uploaded: latest.uploaded,
    queuedCount: run.requests.length,
    queuedNoun:
      run.kind === "checklists"
        ? "actions"
        : run.kind === "autosaves"
          ? "autosaves"
          : "saves",
  };
};

const RowActions = ({
  req,
  onRetry,
  onRemove,
}: {
  req: IPendingRequest;
  onRetry: (reqId: string) => void;
  onRemove: (reqId: string) => void;
}) => (
  <Flex align="center" gap={8} style={{ flexShrink: 0 }}>
    {req.queuedCount && req.queuedCount > 1 ? (
      <Tooltip
        {...pendingTooltipProps}
        disabled={req.status === "done"}
        label={`${req.queuedCount} ${req.queuedNoun} in this consecutive group, oldest first.`}
        multiline
        w={240}>
        <Badge
          size="xs"
          color={req.status === "done" ? "green" : "gray"}
          variant="outline"
          style={req.status === "done" ? { cursor: "default" } : undefined}>
          {req.queuedCount} {req.queuedNoun}
        </Badge>
      </Tooltip>
    ) : null}
    {req.isOtherUser ? (
      <Tooltip
        {...pendingTooltipProps}
        label="Queued by another user. They need to sign in here to upload it."
        multiline
        w={240}>
        <Badge size="xs" color="gray" variant="light">
          Other user
        </Badge>
      </Tooltip>
    ) : null}
    <UploadStatusBadge req={req} />
    {!req.isOtherUser && isRetryable(req.status) ? (
      <>
        <Button
          size="compact-xs"
          variant="light"
          onClick={event => {
            event.stopPropagation();
            onRetry(req.req);
          }}>
          Retry
        </Button>
        <ConfirmationButton
          modalText="Remove this failed request? It will be deleted from the pending list and will not be retried."
          onConfirm={() => onRemove(req.req)}
          zIndex={TOOLTIP_Z_INDEX}
          buttonProps={{
            size: "compact-xs",
            variant: "subtle",
            color: "red",
          }}>
          Remove
        </ConfirmationButton>
      </>
    ) : null}
  </Flex>
);

const NestedRowMarker = () => (
  <Box
    ml={10}
    py={6}
    style={{
      display: "flex",
      alignSelf: "stretch",
      flexShrink: 0,
    }}>
    <Box
      w={2}
      style={{
        alignSelf: "stretch",
        backgroundColor: "var(--mantine-color-gray-4)",
        borderRadius: 1,
      }}
    />
  </Box>
);

const UploadRow = ({
  req,
  onNavigate,
  onRetry,
  onRemove,
  nested = false,
}: {
  req: IPendingRequest;
  onNavigate: (route: string | null | undefined, req: IPendingRequest) => void;
  onRetry: (reqId: string) => void;
  onRemove: (reqId: string) => void;
  nested?: boolean;
}) => {
  const label = req.action;
  const createdLabel = dayjs(req.created).format("D MMM YYYY HH:mm");
  const uploadedLabel =
    req.uploaded && req.status === "done"
      ? dayjs(req.uploaded).format("D MMM YYYY HH:mm")
      : null;

  return (
    <Flex
      align="center"
      justify="space-between"
      gap="sm"
      py={4}
      px={6}
      className={styles.row}
      style={{ cursor: req.route ? "pointer" : undefined }}
      onClick={() => {
        if (req.route) onNavigate(req.route, req);
      }}>
      <Flex
        align="center"
        gap={10}
        style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
        {nested ? <NestedRowMarker /> : null}
        <Flex direction="column" gap={2} style={{ flex: 1, minWidth: 0 }}>
          {req.route ? (
            <Anchor
              component="button"
              type="button"
              size="xs"
              ta="left"
              lineClamp={2}
              onClick={event => {
                event.stopPropagation();
                onNavigate(req.route, req);
              }}>
              {label}
            </Anchor>
          ) : (
            <Text size="xs" lineClamp={2}>
              {label}
            </Text>
          )}
          <Text size="xs" c="dimmed">
            {createdLabel}
            {uploadedLabel ? ` · Uploaded ${uploadedLabel}` : ""}
          </Text>
          {req.status === "failed" && req.error ? (
            <Tooltip
              {...pendingTooltipProps}
              label={req.error}
              multiline
              w={280}>
              <Text size="xs" c="red" lineClamp={2}>
                {req.error}
              </Text>
            </Tooltip>
          ) : null}
        </Flex>
      </Flex>
      <RowActions req={req} onRetry={onRetry} onRemove={onRemove} />
    </Flex>
  );
};

const LogbookHeaderRow = ({
  logbookName,
  route,
  onNavigate,
}: {
  logbookName: string;
  route?: string | null;
  onNavigate: (route: string | null | undefined) => void;
}) => (
  <Flex
    align="center"
    justify="space-between"
    gap="sm"
    py={4}
    px={6}
    className={styles.row}
    style={{ cursor: route ? "pointer" : undefined }}
    onClick={() => {
      if (route) onNavigate(route);
    }}>
    <Flex style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
      {route ? (
        <Anchor
          component="button"
          type="button"
          size="xs"
          fw={600}
          ta="left"
          lineClamp={2}
          onClick={event => {
            event.stopPropagation();
            onNavigate(route);
          }}>
          {logbookName}
        </Anchor>
      ) : (
        <Text size="xs" fw={600} lineClamp={2}>
          {logbookName}
        </Text>
      )}
    </Flex>
  </Flex>
);

const PendingRequestsInfo = () => {
  const [opened, setOpened] = useState(false);
  const navigate = useNavigate();

  const pendingRequests = useSelector(
    state => state.pendingRequestsSlice.pendingRequests,
  );

  const displayItems = groupPendingRequests(pendingRequests);

  const uploadSummary = useMemo(() => {
    const pendingCount = pendingRequests.filter(req =>
      ["pending", "uploading", "failed"].includes(req.status),
    ).length;
    const waitingCount = pendingRequests.filter(
      req => req.status === "pending-new-entry",
    ).length;
    const doneCount = pendingRequests.filter(
      req => req.status === "done",
    ).length;

    const parts: string[] = [];
    if (pendingCount > 0) parts.push(`Pending: ${pendingCount}`);
    if (waitingCount > 0) parts.push(`Waiting: ${waitingCount}`);

    if (parts.length > 0) {
      return `Requests: ${pendingRequests.length} (${parts.join(", ")})`;
    }

    return `Requests: ${pendingRequests.length}${doneCount > 0 ? ` · Done: ${doneCount}` : ""}`;
  }, [pendingRequests]);

  const hasCompletedRequests = useMemo(
    () => pendingRequests.some(req => req.status === "done"),
    [pendingRequests],
  );

  const toggle = useCallback(() => {
    setOpened(prev => !prev);
  }, []);

  const handleNavigate = useCallback(
    (route: string | null | undefined) => {
      if (!route) {
        return;
      }
      setOpened(false);
      navigate(route);
    },
    [navigate],
  );

  const handleRetry = useCallback(async (reqId: string) => {
    if (!window.api?.retryUpload) {
      return;
    }

    await window.api.retryUpload(reqId);
  }, []);

  const handleRemove = useCallback(async (reqId: string) => {
    if (!window.api?.removeFailedUpload) {
      return;
    }

    try {
      await window.api.removeFailedUpload(reqId);
    } catch (error) {
      Notification(
        "Request not removed",
        error instanceof Error
          ? error.message
          : "Unable to remove this request.",
        "red",
      );
    }
  }, []);

  const handleClearCompleted = useCallback(async () => {
    if (!window.api?.clearCompletedRequests) {
      return;
    }

    await window.api.clearCompletedRequests();
  }, []);

  if (pendingRequests.length === 0) {
    return null;
  }

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      keepMounted
      zIndex={POPOVER_Z_INDEX}>
      <Popover.Target>
        <Button ml="auto" variant="white" onClick={toggle}>
          {uploadSummary}
        </Button>
      </Popover.Target>
      <Popover.Dropdown
        maw={520}
        mah={420}
        style={{ overflowY: "auto", width: "100%" }}>
        {hasCompletedRequests ? (
          <>
            <Flex justify="flex-end" pb="xs">
              <ConfirmationButton
                modalText="Are you sure you want to clear out completed requests ?"
                onConfirm={handleClearCompleted}
                zIndex={TOOLTIP_Z_INDEX}
                buttonProps={{ size: "compact-xs", variant: "subtle" }}>
                Clear completed
              </ConfirmationButton>
            </Flex>
            <Divider />
          </>
        ) : null}
        {displayItems.map((item, index) => {
          const isLast = index === displayItems.length - 1;

          if (item.kind === "row") {
            return (
              <Fragment key={item.req.req}>
                <UploadRow
                  req={item.req}
                  onNavigate={handleNavigate}
                  onRetry={handleRetry}
                  onRemove={handleRemove}
                />
                {isLast ? null : <Divider />}
              </Fragment>
            );
          }

          const { group } = item;
          const headerRoute =
            group.runs
              .flatMap(run => run.requests)
              .find(request => request.route)?.route ?? null;
          const hasHeader = Boolean(group.logbookName);

          return (
            <Fragment key={`group-${group.entryId}`}>
              {group.logbookName ? (
                <LogbookHeaderRow
                  logbookName={group.logbookName}
                  route={headerRoute}
                  onNavigate={route => {
                    if (!route) return;
                    setOpened(false);
                    navigate(route);
                  }}
                />
              ) : null}
              {group.runs.map(run => (
                <UploadRow
                  key={run.requests[0].req}
                  req={getRunRequest(run)}
                  nested={hasHeader}
                  onNavigate={handleNavigate}
                  onRetry={handleRetry}
                  onRemove={handleRemove}
                />
              ))}
              {isLast ? null : <Divider />}
            </Fragment>
          );
        })}
      </Popover.Dropdown>
    </Popover>
  );
};

export default PendingRequestsInfo;
